import uuidv4 from 'uuid/v4';
import _ from 'lodash';
import basic from './common/basic.js';
import updater from '../updater.js';
import { unwrapMention, setMentionContent } from '../mention-helper.js';

/* State example:
state: {
  "byId": {
    "attr_0": {
      "id": "attr_0",
      "size": "full",
      "name": "Age",
      "content": { value: "52", type: "string" }
    },
    "attr_1": {
      "id": "attr_1",
      "size": "full",
      "name": "Description",
      "content": { value: "A seasoned pirate, tall but fragile, he always wears his trusty cutlass on his belt.", type: "string" }
    },
    "attr_2": {
      "id": "attr_2",
      "size": "half",
      "name": "Full name",
      "content": { value: "Jim Hawkins", type: "string" }
    },
    "attr_3": {
      "id": "attr_3",
      "size": "half",
      "name": "Age",
      "content": { value: "15", type: "string" }
    },
    "attr_4": {
      "id": "attr_4",
      "size": "full",
      "name": "Class",
      "value": { "data": "Human", type: "string", rich: false } // rich default is true
    }
  }
}
*/
const NEW_ATTRIBUTE = {
  size: 'full',
  name: '',
  value: {
    data: '',
    type: 'string',
    // plain: false, // default
  },
};

const options = {};

const state = { byId: {} };

const getters = {
  ...basic.getters(options),

  /**
   * Get the components of this attribute
   * @param   {string}  id  The attribute id
   * @returns {array}       The components in an array of the form { id, component }
   */
  getComponents(state) {
    return id => {
      return state.byId[id].value.data;
    };
  },

  /**
   * Get the assets of this attribute
   * @param   {string}  id  The asseet id
   * @returns {array}       The assets in an array of the form { id, asset }
   */
  getAssets(state) {
    return id => {
      return state.byId[id].value.data;
    };
  },
};

const actions = {
  ...basic.actions({
    type: 'attributes',
    container: {
      type: null, // 'components' or 'elements'
      prop: 'attributes',
    },
  }),

  /**
   * Create an attribute
   * @param {String}  containerId   The container id in which to put the attribute
   * @param {String}  containerType The container type in which to put the attribute
   * @param {Object}  props         Any properties overriding the default ones
   * @param {Number}  tIndex        The index in the container
   * @param {Boolean} apply         Whether to apply the patch after finishing
   */
  customCreate(context, { containerId, containerType, props, tIndex, apply }) {
    const id = uuidv4();
    const attribute = {
      id,
      ...NEW_ATTRIBUTE,
      ...props,
      cId: containerId,
      cType: containerType,
    };

    context.dispatch('create', {
      node: attribute,
      tId: containerId,
      container: {
        type: containerType,
        prop: 'attributes',
      },
      tIndex,
      apply: false,
    });
    updater.complete({ apply });
    return Promise.resolve(id);
  },

  /**
   * Update some of the attribute's properties
   * @param {String}  id      The attribute id
   * @param {Object}  props   The properties that are new or changed
   * @param {Boolean} apply   Whether to apply the patch after finishing
   */
  customUpdate(context, { id, props, apply }) {
    console.log('Updating attribute with id:', id, _.cloneDeep(props));
    context.dispatch('update', { id, props, apply: false });
    updater.complete({ apply });
  },

  /**
   * Delete an attribute
   * @param {String}  id      The attribute id
   * @param {Booelan} apply   Whether to apply the patch after finishing
   */
  customDelete(context, { id, apply }) {
    context.dispatch('delete', { id, apply: false });
    updater.complete({ apply });
  },

  /**
   * Add a component reference to an attribute 'components' list
   * @param {String}  id          The attribute id
   * @param {String}  componentId The component id
   * @param {Number}  index       The index in the list
   * @param {Boolean} apply       Whether to apply the patch
   */
  addComponent(context, { id, componentId, index, apply }) {
    const patch = [
      {
        op: 'add',
        path: `/data/attributes/byId/${id}/value/data/${index}`,
        value: { id: uuidv4(), component: componentId },
      },
    ];
    updater.add(patch);
    updater.complete({ apply });
  },

  /**
   * Add an asset reference to an attribute 'assets' list
   * @param {String}  id          The attribute id
   * @param {String}  assetId     The asset id
   * @param {Number}  index       The index in the list
   * @param {Boolean} apply       Whether to apply the patch
   */
  addAsset(context, { id, assetId, index, apply }) {
    const patch = [
      {
        op: 'add',
        path: `/data/attributes/byId/${id}/value/data/${index}`,
        value: { id: uuidv4(), asset: assetId },
      },
    ];
    updater.add(patch);
    updater.complete({ apply });
  },

  /**
   * Remove a component reference from an attribute's 'components' list
   * @param {String}  id      The attribute id
   * @param {String}  index   The index to remove
   * @param {Boolean} apply   Whether to apply the patch after finishing
   */
  deleteComponent(context, { id, index, apply }) {
    const patch = [
      {
        op: 'remove',
        path: `/data/attributes/byId/${id}/value/data/${index}`,
      },
    ];

    updater.add(patch);
    updater.complete({ apply });
  },

  /**
   * Remove an asset reference from an attribute's 'assets' list
   * @param {String}  id      The attribute id
   * @param {String}  index   The index to remove
   * @param {Boolean} apply   Whether to apply the patch after finishing
   */
  deleteAsset(context, { id, index, apply }) {
    const patch = [
      {
        op: 'remove',
        path: `/data/attributes/byId/${id}/value/data/${index}`,
      },
    ];

    updater.add(patch);
    updater.complete({ apply });
  },

  /**
   * Remove component references from all attributes 'components' list
   * @param {String}  ids      The component ids
   * @param {Boolean} apply   Whether to apply the patch after finishing
   */
  deleteComponents(context, { ids, apply }) {
    const patch = [];
    // Remove the component from any component list
    Object.values(context.state.byId)
      .filter(
        attribute =>
          attribute.value.type === 'component-list' &&
          attribute.value.data.some(c => ids.includes(c.component))
      )
      .forEach(attribute => {
        const components = attribute.value.data;
        // Search from the end - since we are doing removal
        let index = components.length;
        // Remove the components looping the component list from end to start
        while (index > 0) {
          index -= 1;

          if (ids.includes(components[index].component)) {
            // Adjust the index (if there are operations on this array already on the patch)
            const adjIndex = updater.getIndexAdjustment(
              `/data/attributes/byId/${attribute.id}/value/data/${index}`
            );

            patch.push({
              op: 'remove',
              path: `/data/attributes/byId/${attribute.id}/value/data/${adjIndex}`,
            });
          }
        }
      });

    updater.add(patch);
    updater.complete({ apply });
  },

  /**
   * Remove asset references from all attributes 'assets' list
   * @param {String}  ids     The asset ids
   * @param {Boolean} apply   Whether to apply the patch after finishing
   */
  deleteAssets(context, { ids, apply }) {
    const patch = [];
    // Remove the asset from any asset list
    Object.values(context.state.byId)
      .filter(
        attribute =>
          attribute.value.type === 'asset-list' &&
          attribute.value.data.some(c => ids.includes(c.asset))
      )
      .forEach(attribute => {
        const assets = attribute.value.data;
        // Search from the end - since we are doing removal
        let index = assets.length;
        // Remove the assets looping the asset list from end to start
        while (index > 0) {
          index -= 1;

          if (ids.includes(assets[index].asset)) {
            // Adjust the index (if there are operations on this array already on the patch)
            const adjIndex = updater.getIndexAdjustment(
              `/data/attributes/byId/${attribute.id}/value/data/${index}`
            );

            patch.push({
              op: 'remove',
              path: `/data/attributes/byId/${attribute.id}/value/data/${adjIndex}`,
            });
          }
        }
      });

    updater.add(patch);
    updater.complete({ apply });
  },

  /**
   * Move a component reference from an attribute 'components' list to another index (or another attribute)
   * @param {String}  sId     The attribute id (the old host)
   * @param {String}  sIndex  The old index
   * @param {String}  tId     The attribute id (the new host)
   * @param {String}  tIndex  The new index
   * @param {Boolean} apply   Whether to apply the patch after finishing
   */
  moveComponent(context, { sId, sIndex, tId, tIndex, apply }) {
    const fromState = _.cloneDeep(context.getters.getComponents(sId));
    const state = _.cloneDeep(context.getters.getComponents(tId));
    const component = fromState[sIndex];
    const index = state.findIndex(el => _.isEqual(el, component));
    if (index !== -1 && index < tIndex) {
      tIndex -= 1;
    }
    const patch = [
      {
        op: 'move',
        from: `/data/attributes/byId/${sId}/value/data/${sIndex}`,
        path: `/data/attributes/byId/${tId}/value/data/${tIndex}`,
        state,
        fromState,
      },
    ];
    updater.add(patch);
    updater.complete({ apply });
  },

  /**
   * Move an asset reference from an attribute 'assets' list to another index (or another attribute)
   * @param {String}  sId     The attribute id (the old host)
   * @param {String}  sIndex  The old index
   * @param {String}  tId     The attribute id (the new host)
   * @param {String}  tIndex  The new index
   * @param {Boolean} apply   Whether to apply the patch after finishing
   */
  moveAsset(context, { sId, sIndex, tId, tIndex, apply }) {
    const fromState = _.cloneDeep(context.getters.getAssets(sId));
    const state = _.cloneDeep(context.getters.getAssets(tId));
    const asset = fromState[sIndex];
    const index = state.findIndex(el => _.isEqual(el, asset));
    if (index !== -1 && index < tIndex) {
      tIndex -= 1;
    }
    const patch = [
      {
        op: 'move',
        from: `/data/attributes/byId/${sId}/value/data/${sIndex}`,
        path: `/data/attributes/byId/${tId}/value/data/${tIndex}`,
        state,
        fromState,
      },
    ];
    updater.add(patch);
    updater.complete({ apply });
  },

  /**
   * Remove mentions in all attributes.
   * @param {array}   ids       The ids of the mentioned objects
   * @param {String}  type      The type of the mentioned objects
   * @param {Boolean} apply     Whether to apply the patch after finishing
   */
  deleteMentions(context, { ids, type, apply }) {
    const patch = [];
    Object.values(context.state.byId).forEach(attribute => {
      if (attribute.value.type === 'string') {
        const text = unwrapMention(attribute.value.data, ids, type);
        if (text !== attribute.value.data) {
          patch.push({
            op: 'replace',
            path: `/data/attributes/byId/${attribute.id}/value/data`,
            value: text,
          });
        }
      }
    });
    updater.add(patch);
    updater.complete({ apply });
  },

  /**
   * Update the name in mentions in all attributes.
   * @param {String}  id       The id of the mentioned object
   * @param {String}  type     The type of the mentioned object
   * @param {String}  name     The new name of the mentioned object
   * @param {String}  label The original label of the mention if different from name
   * @param {Boolean} apply    Whether to apply the patch after finishing
   */
  updateMentions(context, { id, type, name, label, apply }) {
    const patch = [];
    Object.values(context.state.byId).forEach(attribute => {
      if (attribute.value.type === 'string') {
        const text = setMentionContent(
          attribute.value.data,
          id,
          type,
          name,
          label
        );
        if (text !== attribute.value.data) {
          patch.push({
            op: 'replace',
            path: `/data/attributes/byId/${attribute.id}/value/data`,
            value: text,
          });
        }
      }
    });
    updater.add(patch);
    updater.complete({ apply });
  },
};

const module = {
  namespaced: true,
  state,
  getters,
  mutations: {},
  actions,
};

export default module;
